/**
 * MembersCard — Settings → Members (Revenue Recovery R4, 0138): which
 * employee each member's login acts as. A link lets the employee a deal is
 * assigned to confirm or dismiss that deal's risk flags through their own
 * login, whatever their role (ruling 2: the admin-set link is the grant).
 *
 * The ExportsCard posture: owner/admin read and write; everyone else reads a
 * sentence and NO request is made (decided from /v1/me, fail closed). The
 * server's 403 stays the boundary, and every refusal it speaks is shown as it
 * was said. Every set and clear carries a reason the server records. The
 * card shows people and links only — no counts, no totals, nothing per
 * person.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchTenantMembers, setMemberEmployee, clearMemberEmployee } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import type { TenantMember, MemberEmployeeOption } from '../../../../api/types'
import type { MeResponse } from '../../types'
import { MEMBERS_GATE_SENTENCE, canManageMembers, memberName, roleLabel, statusLabel, linkLine, canSet, canClear, pickerOptions } from './members'

const inputCls = 'px-3 py-2 border border-edge-control rounded-control text-13 text-ink-primary placeholder-ink-section-label'

export function MembersCard({ me }: { me: Pick<MeResponse, 'role'> | null }) {
  return (
    <div className="k-card">
      <div className="p-5 flex flex-col gap-4">
        <div>
          <div className="text-13 font-semibold text-ink-primary">Members</div>
          <div className="text-12 text-ink-muted mt-1">
            Link a login to the employee it belongs to, so that person can confirm or dismiss the risk flags on deals assigned to them. Every link and every change to it is recorded with its reason.
          </div>
        </div>
        {canManageMembers(me)
          ? <MembersList />
          : <div className="text-12 text-ink-muted" data-members-gate>{MEMBERS_GATE_SENTENCE}</div>}
      </div>
    </div>
  )
}

function MembersList() {
  const [version, setVersion] = useState(0)
  const q = useAsync(() => fetchTenantMembers(), [version])
  if (q.loading) return <LoadingState rows={3} />
  if (q.error) return <ErrorState message={q.error} />
  const data = q.data!
  if (data.items.length === 0) return <EmptyState message="No members" hint="Members appear here once they have signed in." />
  return (
    <table className="dash-table w-full" aria-label="Members">
      <thead>
        <tr>
          <th className="text-left">Member</th>
          <th className="text-left">Role</th>
          <th className="text-left">Status</th>
          <th className="text-left">Acts as</th>
          <th className="text-left">Change</th>
        </tr>
      </thead>
      <tbody>
        {data.items.map(m => <MemberRow key={m.membershipId} member={m} members={data.items} employees={data.employees} onChanged={() => setVersion(v => v + 1)} />)}
      </tbody>
    </table>
  )
}

function MemberRow({ member, members, employees, onChanged }: {
  member: TenantMember
  members: readonly TenantMember[]
  employees: readonly MemberEmployeeOption[]
  onChanged: () => void
}) {
  const name = memberName(member)
  const [employeeId, setEmployeeId] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const options = pickerOptions(employees, members)

  async function run(write: () => Promise<unknown>, done: string, fallback: string) {
    if (busy) return
    setBusy(true)
    try {
      await write()
      toast.success(done)
      onChanged()
    } catch (e) {
      toast.error(apiErrorMessage(e, fallback))
    } finally {
      setBusy(false)
    }
  }

  const reasonOk = reason.trim() !== ''
  return (
    <tr data-member={member.membershipId}>
      <td>{name}</td>
      <td>{roleLabel(member.role)}</td>
      <td>{statusLabel(member.status)}</td>
      <td data-acts-as>{linkLine(member)}</td>
      <td>
        {canSet(member) && (
          <div className="flex items-center gap-2 flex-wrap">
            <select className={inputCls} aria-label={`Employee for ${name}`} value={employeeId} disabled={busy} onChange={e => setEmployeeId(e.target.value)}>
              <option value="">Choose an employee</option>
              {options.map(o => <option key={o.id} value={o.id} disabled={o.disabled}>{o.label}</option>)}
            </select>
            <input className={inputCls} aria-label={`Reason for linking ${name}`} placeholder="Reason (recorded)" value={reason} disabled={busy} onChange={e => setReason(e.target.value)} />
            <button type="button" className="k-btn-secondary" disabled={busy || employeeId === '' || !reasonOk}
              onClick={() => void run(() => setMemberEmployee(member.membershipId, employeeId, reason.trim()), 'Login linked', 'Failed to link this login')}>
              {busy ? 'Saving…' : 'Link'}
            </button>
          </div>
        )}
        {canClear(member) && (
          <div className="flex items-center gap-2 flex-wrap">
            <input className={inputCls} aria-label={`Reason for clearing ${name}'s link`} placeholder="Reason (recorded)" value={reason} disabled={busy} onChange={e => setReason(e.target.value)} />
            <button type="button" className="k-btn-secondary" disabled={busy || !reasonOk}
              onClick={() => void run(() => clearMemberEmployee(member.membershipId, reason.trim()), 'Link cleared', 'Failed to clear this link')}>
              {busy ? 'Saving…' : 'Clear link'}
            </button>
          </div>
        )}
        {!canSet(member) && !canClear(member) && <span className="text-12 text-ink-muted">Only an active member is linked</span>}
      </td>
    </tr>
  )
}
